import { FC, PropsWithChildren } from "react";

import clsx from "clsx";

import { RenderWhen, TooltipIcon } from "../../../../../../components";
import {
  AnalysisAdditionalInfo,
  AnalysisStatus
} from "../../../../../../lib/enums";
import { RLcsAnalysisResponseData } from "../../../rlcloudsandbox/hooks/useRlcsAnalysisStatus";
import Analyzing from "../../components/card/subtitle/Analyzing";
import Configure from "../../components/card/subtitle/Conigure";
import Loading from "../../components/card/subtitle/Loading";
import styles from "../../components/card/subtitle/card-subtitle.module.scss";
import { WarningIcon } from "../../components/icons";
import { getCloudSandboxAnalyzingCount } from "./utils";

type Props = {
  info?: string;
  status?: RLcsAnalysisResponseData;
  impactsFinalClassification?: boolean;
  isLoading: boolean;
  hasData?: boolean;
  onAnalyze?: () => void;
};

const Subtitle: FC<
  PropsWithChildren<Omit<Props, "impactsFinalClassification">>
> = ({ children, isLoading, info, status, hasData, onAnalyze }) => {
  const isOversized = info === AnalysisAdditionalInfo.FILE_OVERSIZED;

  const handleAnalyze = () => {
    if (isOversized) return;
    onAnalyze?.();
  };

  const { automated, interactive } = status ?? {};

  const isFailedUpload = [
    automated?.display_status,
    interactive?.display_status
  ].every(item => item === AnalysisStatus.FAILED_UPLOAD);

  const showAnalyzeButton =
    info === AnalysisAdditionalInfo.NEVER_SUBMITED ||
    info !== AnalysisAdditionalInfo.NOT_CONFIGURED ||
    isFailedUpload;

  const analyzingCount = getCloudSandboxAnalyzingCount(status);

  if (analyzingCount)
    return <Analyzing label={`Analyzing ${analyzingCount > 1 ? "(2)" : ""}`} />;

  if (isLoading && !hasData) return <Loading />;

  if (hasData) return <>{children}</>;

  if (info === AnalysisAdditionalInfo.NOT_CONFIGURED)
    return <Configure type="INTEGRATION" />;

  if (showAnalyzeButton)
    return (
      <span
        className={clsx(styles.analyze_button, isOversized && styles.disabled)}
        onClick={handleAnalyze}
        data-testid="report_summary_subheader_analyze_link"
      >
        Analyze
      </span>
    );

  return <></>;
};

const CardSubtitle: FC<PropsWithChildren<Props>> = ({
  children,
  info,
  status,
  isLoading,
  hasData,
  impactsFinalClassification = false,
  onAnalyze
}) => (
  <div className="flex align-center flex-gap-2">
    <Subtitle
      isLoading={isLoading}
      status={status}
      info={info}
      hasData={hasData}
      onAnalyze={onAnalyze}
    >
      {children}
    </Subtitle>
    <RenderWhen condition={info === AnalysisAdditionalInfo.FILE_OVERSIZED}>
      <WarningIcon
        className={styles.icon}
        message="File size is over size limit."
      />
    </RenderWhen>
    <RenderWhen condition={!impactsFinalClassification}>
      <TooltipIcon
        className={styles.info_icon}
        icon="info-circle"
        message="Does not impact final classification."
      />
    </RenderWhen>
  </div>
);

export default CardSubtitle;
